import {
  FC,
  ChangeEventHandler,
  MutableRefObject,
  memo,
  FormEventHandler,
} from "react";
import { NumericFormat } from "react-number-format";
import { useTranslation } from "react-i18next";
import { suffixNumber } from "../../../utils/suffixNumber";
import { Button, ButtonType } from "../../../components/Button";
import { StyledInput, Suffix, Wrapper, ClickAbled } from "./styled";
import { BetType } from "../../../types/common";
import Loading from "../../../components/Icons/Loading";
import { Currency } from "../../../types/member";
import { CONFIG } from "../../../config";
import { MAX_PAYOUT } from "../../../constants/common";

export type PlaceBetFormProps = {
  isLoading?: boolean;
  amount?: number;
  betType?: BetType;
  pairs?: number;
  min?: number;
  max?: number;
  maxReturn?: number;
  isAcceptAnyOdds?: boolean;
  isForceToWaiting?: boolean;
  oddsSummary?: number;
  isLive?: boolean;
  onClickMinMax?: (amount: number) => void;
  onCancel?: () => void;
  onSubmit?: FormEventHandler<HTMLFormElement>;
  onChangeAmount?: ChangeEventHandler<HTMLInputElement>;
  onBlurAmount?: ChangeEventHandler<HTMLInputElement>;
  onChangeAcceptAnyOdds?: ChangeEventHandler<HTMLInputElement>;
  onChangeIsForceToWaiting?: ChangeEventHandler<HTMLInputElement>;
  InputRef?: MutableRefObject<HTMLInputElement | undefined>;
  currency?: Currency;
};

const PlaceBetForm: FC<PlaceBetFormProps> = ({
  amount = 0,
  min = 0,
  max = 0,
  maxReturn,
  betType = "normal",
  oddsSummary = 1,
  isLive = false,
  isLoading = false,
  isAcceptAnyOdds = false,
  isForceToWaiting = false,
  onClickMinMax = () => {},
  onCancel,
  pairs = 0,
  onSubmit,
  onChangeAmount,
  onBlurAmount,
  onChangeAcceptAnyOdds,
  onChangeIsForceToWaiting,
  InputRef,
  currency = "THB",
}) => {
  const { t } = useTranslation();
  const isMultipleMatch = betType === "step" || betType === "comboStep";
  const maxBet =
    isMultipleMatch ? parseInt(`${MAX_PAYOUT / oddsSummary}`) : max;
  const handleClickMin = () => {
    onClickMinMax(min);
  };

  const handleClickMax = () => {
    onClickMinMax(maxBet);
  };
  const maxPayout =
    (maxReturn || amount) > MAX_PAYOUT ? MAX_PAYOUT : maxReturn || amount;

  return (
    <Wrapper
      isLive={isLive}
      noValidate
      onSubmit={onSubmit}
      className="p-4 flex items-start flex-col">
      <label className="cursor-pointer mb-1">
        <input
          type="checkbox"
          className="mr-1"
          checked={isAcceptAnyOdds}
          onChange={onChangeAcceptAnyOdds}
          disabled={isLoading}
        />
        Accept any odds
      </label>
      {CONFIG.IS_TEST_WAITING_TICKET && (
        <label className="cursor-pointer mb-1">
          <input
            type="checkbox"
            className="mr-1"
            checked={isForceToWaiting}
            onChange={onChangeIsForceToWaiting}
            disabled={isLoading}
          />
          Force to waiting
        </label>
      )}

      <div className="relative">
        <NumericFormat
          getInputRef={InputRef}
          className="text-right py-2 pl-2 pr-12 box-border rounded-lg w-full"
          value={amount}
          thousandSeparator=","
          onChange={onChangeAmount}
          onBlur={onBlurAmount}
          customInput={StyledInput}
          disabled={isLoading}
        />
        <Suffix>{currency}</Suffix>
      </div>

      {isMultipleMatch && (
        <div className="flex justify-between w-full">
          <div className="text-sm">{t("Matches")}</div>
          <div className="text-sm">{pairs}/12</div>
        </div>
      )}

      {isMultipleMatch && (
        <div className="flex justify-between w-full">
          <div className="text-sm">
            {betType === "step" ? "Parlay" : "Combo"}
          </div>
          <div className="text-sm">@{oddsSummary}</div>
        </div>
      )}

      <div className="flex justify-between w-full">
        <div>
          <ClickAbled disabled={isLoading} onClick={handleClickMin}>
            {t("Min")}
          </ClickAbled>{" "}
          -{" "}
          <ClickAbled disabled={isLoading} onClick={handleClickMax}>
            {t("Max")}
          </ClickAbled>
        </div>
        <div>
          <ClickAbled disabled={isLoading} onClick={handleClickMin}>
            {suffixNumber(min)}
          </ClickAbled>{" "}
          -{" "}
          <ClickAbled disabled={isLoading} onClick={handleClickMax}>
            {suffixNumber(maxBet)}
          </ClickAbled>
        </div>
      </div>

      <div className="flex justify-between w-full mb-2">
        <div className="text-sm">{t("Max payout")}</div>
        <div className="text-sm">{suffixNumber(maxPayout)}</div>
      </div>

      <div className="flex justify-between w-full gap-2">
        <Button
          disabled={isLoading}
          onClick={onCancel}
          className="flex-1 p-2 text-sm flex justify-center items-center"
          buttonType={ButtonType.PRIMARY}
          type="button">
          {isLoading && <Loading />}
          {t("Cancel")}
        </Button>
        <Button
          disabled={isLoading}
          className="flex-1 p-2 text-sm flex justify-center items-center"
          buttonType={ButtonType.SECONDARY}
          type="submit">
          {isLoading && <Loading />}
          {t("Bet")}
        </Button>
      </div>
    </Wrapper>
  );
};

export default memo(PlaceBetForm);
